import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import type { NewDeliverableInput } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES } from '../../types/portal';
import { deliverableStage, isVersioned, stageHeading } from '../../utils/deliverables';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
import { TranscriptUpload } from './TranscriptUpload';
const EMPTY: NewDeliverableInput = {
  name: '',
  type: 'Video',
  quantity: '1',
  status: 'Not Started',
  version: '',
  driveUrl: '',
  audioUrl: '',
  internalNote: '',
  visibleToClient: false,
  transcript: ''
};
export function AddDeliverable() {
  const {
    addDeliverable,
    notify
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState<NewDeliverableInput>(EMPTY);
  function edit(patch: Partial<NewDeliverableInput>) {
    setInput(current => ({
      ...current,
      ...patch
    }));
  }
  function submit(event: React.FormEvent) {
    event.preventDefault();
    const name = input.name.trim();
    if (!name) return;
    /** Versions only exist once the status reaches Review or Final Delivery. */
    addDeliverable({
      ...input,
      name,
      version: isVersioned(input.status) ? input.version : ''
    });
    notify(`${name} added`);
    setInput(EMPTY);
    setOpen(false);
  }
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-8 inline-flex items-center gap-3`} data-id="element-135" data-mp="17odgcsydia88">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-136" data-mp="1hg9g08g3y8ek" />
        Add Deliverable
      </button>;
  }
  return <form onSubmit={submit} className="mt-8 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-137" data-mp="1chps42hyemjk">
      <h3 className="font-display text-2xl leading-none" data-id="element-138" data-mp="1qfo636sd4gyj">New Deliverable</h3>

      <label className="mt-8 block" data-id="element-139" data-mp="1lhmnemyumjnl">
        <span className="label block text-paper/40" data-id="element-140" data-mp="1lieb7bm7fd3b">Name</span>
        <input value={input.name} onChange={event => edit({
        name: event.target.value
      })} placeholder="e.g. Speaker Interview 01" autoFocus className={FIELD} data-id="element-141" data-mp="j46vpyif11e0" />
      </label>

      <div className="mt-8 grid grid-cols-2 gap-8 sm:grid-cols-3" data-id="element-142" data-mp="s3k6cui9zw0b">
        <label className="block" data-id="element-143" data-mp="wv3deuaszy6a">
          <span className="label block text-paper/40" data-id="element-144" data-mp="ww505w16m93u">Type</span>
          <select value={input.type} onChange={event => edit({
          type: event.target.value as NewDeliverableInput['type']
        })} className={SELECT} data-id="element-145" data-mp="11uojrpsaz8t1">
            {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-146" data-mp="1bt43v8swxht1">
                {option}
              </option>)}
          </select>
        </label>
        <label className="block" data-id="element-147" data-mp="3dkh9k02pftq5">
          <span className="label block text-paper/40" data-id="element-148" data-mp="3dljxq08n7i2d">Quantity</span>
          <input value={input.quantity} onChange={event => edit({
          quantity: event.target.value
        })} placeholder="e.g. 8" className={FIELD} data-id="element-149" data-mp="3hksqp2vuzbwv" />
        </label>
        {isVersioned(input.status) ? <label className="block" data-id="element-150" data-mp="359i7rvhkk0lh">
            <span className="label block text-paper/40" data-id="element-151" data-mp="30bgopak581a0">Version</span>
            <input value={input.version} onChange={event => edit({
          version: event.target.value
        })} placeholder="e.g. V1" className={FIELD} data-id="element-152" data-mp="2wan16iam4ohn" />
          </label> : <div className="block" data-id="element-153" data-mp="3o2vwvjylpct6">
            <span className="label block text-paper/40" data-id="element-154" data-mp="3nzq5fy2i1yua">Version</span>
            <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper/30" data-id="element-155" data-mp="3jywhx5syym1x">
              Added once In Review
            </p>
          </div>}
      </div>

      <label className="mt-8 block" data-id="element-156" data-mp="28m3v89f1cnsq">
        <span className="label block text-paper/40" data-id="element-157" data-mp="28izjmqz8qps1">Status</span>
        <select value={input.status} onChange={event => edit({
        status: event.target.value as NewDeliverableInput['status']
      })} className={SELECT} data-id="element-158" data-mp="23l6dfxxxtvrr">
          {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-159" data-mp="5uf8jsgn922">
              {option}
            </option>)}
        </select>
        {/* Stage follows the status, so there is nothing else to set. */}
        <span className="label-sm mt-3 block text-paper/30" data-id="element-160" data-mp="e40roay8w68p">
          Stage · {stageHeading(deliverableStage(input.status))} · set from status
        </span>
      </label>

      {input.type === 'Transcript' ? <TranscriptUpload value={input.transcript ?? ''} onChange={text => edit({
      transcript: text
    })} data-id="element-161" data-mp="53m4t75muj33" /> : <label className="mt-8 block" data-id="element-162" data-mp="a0ckhtdizcnz">
          <span className="label block text-paper/40" data-id="element-163" data-mp="2ptl833bqg3zd">Google Drive Link</span>
          <input value={input.driveUrl} onChange={event => edit({
        driveUrl: event.target.value
      })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-164" data-mp="2pvo72cuv57g8" />
        </label>}

      <label className="mt-8 block" data-id="element-165" data-mp="2i8r5powk8uek">
        <span className="label block text-paper/40" data-id="element-166" data-mp="2ibvhb7ccusf9">Notes</span>
        <input value={input.internalNote} onChange={event => edit({
        internalNote: event.target.value
      })} placeholder="Optional internal note" className={FIELD} data-id="element-167" data-mp="2w8imfxegoow9" />
      </label>

      <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-168" data-mp="23l611qk1en4j">
        <span className="label text-paper/40" data-id="element-169" data-mp="1ynmm0ofim6bj">Visible to Client</span>
        <button type="button" onClick={() => edit({
        visibleToClient: !input.visibleToClient
      })} aria-pressed={input.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', input.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-170" data-mp="2dbr6nxy4orwy">
          {input.visibleToClient ? 'On' : 'Off'}
        </button>
      </label>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-171" data-mp="3kujthii1ze30">
        <button type="submit" className={BUTTON_SOLID} data-id="element-172" data-mp="3pofpiferaqvj">
          Add Deliverable
        </button>
        <button type="button" onClick={() => {
        setInput(EMPTY);
        setOpen(false);
      }} className={BUTTON_QUIET} data-id="element-173" data-mp="3tnwvd9y3hq18">
          Cancel
        </button>
      </div>
    </form>;
}